import Address from "@/types/Address.interface"
import Brand from "@/types/Brand.interface"
import ExtensionOption from "@/types/ExtensionOption.interface"
import Input, { InputType } from "@/types/Input.interface"
import Person, { Email } from "@/types/Person.interface"
import UnitedState from "@/types/State.interface"
import StockExchange from "@/types/StockExchange.interface"
import Template from "@/types/Template.interface"

export default {
    getters: {
        addressInputs: (state, getters) => (address: Address): Input[] => {
            //  Return address inputs
            return [
                {
                    type: InputType.TEXT,
                    name: 'Line 1',
                    label: 'Address Line 1',
                    path: 'line1',
                    validate: 'required|max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'Line 2',
                    label: 'Address Line 2',
                    path: 'line2',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'City',
                    label: 'City',
                    path: 'city',
                    validate: 'required|max:45'
                },
                {
                    type: InputType.SELECT,
                    name: 'State',
                    label: 'State',
                    path: 'country_subdivision',
                    itemText: 'stabrv',
                    itemValue: 'stabrv',
                    validate: 'required|max:45',
                    options: getters.states,
                    selected: getters.states.find(
                        (x: UnitedState) => x.stabrv == address.country_subdivision
                    )
                },
                {
                    type: InputType.TEXT,
                    name: 'Postal Code',
                    label: 'Postal Code',
                    validate: 'max:10',
                    path: 'postalcode'
                }
            ]
        },
        brandAddressInputs(_, getters): Input[] {
            return [
                {
                    type: InputType.SELECT,
                    name: 'Country',
                    label: 'Country*',
                    path: 'country',
                    options: ['United States', 'Canada'],
                    class: 'pa-2',
                    validate: 'required',
                    cols: 2
                },
                {
                    type: InputType.TEXT,
                    name: 'Line 1',
                    label: 'Address Line 1*',
                    path: 'line1',
                    class: 'pa-2',
                    validate: 'required|max:300',
                    required: true,
                    cols: 5
                },
                {
                    type: InputType.TEXT,
                    name: 'Line 2',
                    label: 'Address Line 2',
                    path: 'line2',
                    class: 'pa-2',
                    validate: 'max:300',
                    cols: 5
                },
                {
                    type: InputType.SELECT,
                    name: 'State',
                    label: 'State',
                    path: 'state',
                    itemText: 'stabrv',
                    itemValue: 'stabrv',
                    class: 'pa-2',
                    validate: 'max:22',
                    options: getters.stateAbrvs,
                    cols: 2,
                },
                {
                    type: InputType.TEXT,
                    name: 'City',
                    label: 'City',
                    path: 'city',
                    class: 'pa-2',
                    validate: 'required|max:191',
                    cols: 3
                },
                {
                    type: InputType.TEXT,
                    name: 'Postal Code',
                    label: 'Zip Code/Postal Code',
                    validate: 'required|max:5',
                    path: 'postal_code',
                    class: 'pa-2',
                    cols: 2
                },
                {
                    type: InputType.TEXT,
                    name: 'Region/Province',
                    label: 'Region/Province',
                    validate: 'max:191',
                    path: 'region',
                    class: 'pa-2',
                    cols: 5
                },
            ]
        },
        brandCompanyInputs: (_, getters) => (selected: Brand, stonkExchanges: StockExchange[], stonksEnabled: boolean): Input[] => {
            return [
                {
                    type: InputType.TEXT,
                    name: 'Company',
                    label: 'Legal Organization Name*',
                    path: 'company_name',
                    class: 'pa-2',
                    validate: 'required|max:191',
                    cols: 4
                },
                {
                    type: InputType.TEXT,
                    name: 'DBA',
                    label: 'DBA',
                    path: 'dba',
                    class: 'pa-2',
                    validate: 'max:191',
                    cols: 4
                },
                {
                    type: InputType.TEXT,
                    name: 'Website',
                    label: 'Website*',
                    path: 'website',
                    validate: 'required|verify_url|max:191',
                    class: 'pa-2',
                    cols: 4
                },
                {
                    type: InputType.TEXT,
                    name: 'Tax ID',
                    label: 'Tax Number/ID/EIN*',
                    path: 'tax_id',
                    class: 'pa-2',
                    validate: 'required|numeric|max:45',
                    cols: 3
                },
                {
                    type: InputType.SELECT,
                    name: 'Alternate Business ID Type',
                    label: 'Alternate Business ID Type',
                    selected: selected?.alt_business_id_type,
                    path: 'alt_business_id_type',
                    class: 'pa-2',
                    options: [
                        'DUNS',
                        'GIIN',
                        'LEI',
                        'OTHER',
                        'N/A'
                    ],
                    cols: 3
                },
                {
                    type: InputType.TEXT,
                    name: 'Alternate Business ID',
                    label: 'Alternate Business ID',
                    path: 'alt_business_id',
                    class: 'pa-2',
                    validate: 'max:191',
                    cols: 3
                },
                {
                    type: InputType.SELECT,
                    name: 'Organization Type',
                    label: 'Organization Type*',
                    validate: 'required',
                    path: 'company_type',
                    selected: selected?.company_type,
                    class: 'pa-2',
                    options: [
                        'Sole Proprietorship',
                        'Partnership',
                        'Corporation',
                        'Co-Operative',
                        'LLC',
                        'Non-Profit'
                    ],
                    cols: 3
                },
                {
                    type: InputType.SELECT,
                    name: 'Vertical Type',
                    label: 'Vertical Type*',
                    validate: 'required|max:45',
                    selected: selected?.vertical,
                    path: 'vertical',
                    class: 'pa-2',
                    options: [
                        'Agriculture',
                        'Communication and Mass Media',
                        'Construction and Materials',
                        'Education',
                        'Energy and Utilities',
                        'Entertainment',
                        'Financial',
                        'Gambling Lottery',
                        'Government',
                        'Healthcare',
                        'Hospitality and Travel',
                        'Insurance',
                        'Manufacturing',
                        'Non Government Organization',
                        'Real Estate',
                        'Retail',
                        'Information Technology'
                    ],
                    cols: 3
                },
                {
                    type: InputType.SELECT,
                    name: 'Organization Status',
                    label: 'Organization Status*',
                    validate: 'required',
                    selected: selected?.company_status,
                    path: 'company_status',
                    class: 'pa-2',
                    options: ['Private', 'Public'],
                    cols: 3
                },
                {
                    type: InputType.TEXT,
                    name: 'Stock Symbol',
                    label: 'Stock Symbol/Ticker',
                    validate: 'max:16',
                    disabled: stonksEnabled,
                    path: 'stock_symbol',
                    class: 'pa-2',
                    cols: 3
                },
                {
                    type: InputType.SELECT,
                    name: 'Stock Exchange',
                    label: 'Stock Exchange',
                    validate: '',
                    selected: selected?.stock_exchange,
                    path: 'stock_exchange',
                    disabled: stonksEnabled,
                    options: stonkExchanges, // they only go up 📈
                    class: 'pa-2',
                    cols: 3
                }
            ]
        },
        brandContactInputs(): Input[] {
            return [
                {
                    type: InputType.TEXT,
                    name: 'Contact Name',
                    label: 'Full Name*',
                    path: 'contact_name',
                    validate: 'required|max:191',
                    required: true,
                    class: 'pa-2',
                    cols: 4
                },
                {
                    type: InputType.TEXT,
                    name: 'Email',
                    label: 'Email*',
                    path: 'contact_email',
                    validate: 'required|max:191|email',
                    required: true,
                    class: 'pa-2',
                    cols: 4
                },
                {
                    type: InputType.PHONE,
                    name: 'Mobile Phone',
                    label: 'Mobile Phone*',
                    path: 'contact_phone_number',
                    mask: '(###) ###-####',
                    validate: 'required|phone',
                    required: true,
                    class: 'pa-2',
                    cols: 4
                }
            ]
        },
        chatbotDefaultInputs(state, getters): Input[] {
            //  Grab our extensions
            const extensions = getters.extensionOptions

            //  Our default fields
            const inputs = [
                {
                    type: InputType.PHONE,
                    name: "Default Phone",
                    label: "Mobile Phone",
                    path: "phone_default.free_form_number",
                    order: 1,
                    validate: 'phone',
                    required: true,
                    disableDelete: true,
                    disableRequired: true,
                    disableInputChange: true
                },
                {
                    type: InputType.TEXT,
                    name: "Title",
                    label: "Title",
                    path: "name_title",
                    order: 2,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "First Name",
                    label: "First Name",
                    path: "name_given",
                    order: 3,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Middle Name",
                    label: "Middle Name",
                    path: "name_middle",
                    order: 4,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Last Name",
                    label: "Last Name",
                    path: "name_family",
                    order: 5,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Full Name",
                    label: "Full Name",
                    path: "name_full",
                    order: 6,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Suffix",
                    label: "Suffix",
                    path: "name_suffix",
                    order: 7,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Billing Address Line 1",
                    label: "Billing Line 1",
                    path: "address_billing.line1",
                    order: 8,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Billing Address Line 2",
                    label: "Billing Line 2",
                    path: "address_billing.line2",
                    order: 9,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Shipping Address Line 1",
                    label: "Address Line 1",
                    path: "address_shipping.line1",
                    order: 10,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Shipping Address Line 2",
                    label: "Address Line 2",
                    path: "address_shipping.line2",
                    order: 11,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Company",
                    label: "Company",
                    path: "company",
                    order: 12,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Website",
                    label: "Website",
                    path: "website",
                    order: 13,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Email",
                    label: "Email",
                    path: "email_default.address",
                    order: 14,
                    validate: 'email',
                    required: true
                },
                {
                    type: "TEXTAREA",
                    name: "Message",
                    label: "Message",
                    path: 'message',
                    order: 15,
                    validate: 'max:300',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Short Answer",
                    label: "Short Answer",
                    path: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
                    order: 16,
                    validate: 'max:300',
                    required: true
                }
            ] as Input[]

            //  If we don't have any extension options, just return our inputs
            if (!extensions) return inputs

            //  Else, iterate through the extension options and add each one
            extensions.forEach((option) => {
                inputs.push({
                    type: option.type.toUpperCase(),
                    name: option.name,
                    label: option.name,
                    path: option.name,
                    options: option.type === 'dropdown' ? option.data : null,
                    cols: 12,
                    order: 16,
                    validate: (
                        {
                            number: 'max:45|numeric',
                            phone: 'phone'
                        }[option.type] || 'max:75'
                    ),
                    required: true
                })
            })

            //  Return all our inputs now
            return inputs
        },
        extensionOptionInputs: (_, getters) => (extension: ExtensionOption): Input[] => {
            //  Get the current user permission and check if they are an operator
            const role = getters.userRole(getters.user)
            const operator = window.getProps(role, ['name']) === 'Operator'

            //  Disable each custom field input if operator is enabled
            return [
                {
                    type: InputType.TEXT,
                    name: 'FieldName',
                    label: 'Field Name',
                    path: 'name',
                    validate: 'required|max:45',
                    disabled: operator
                },
                {
                    type: InputType.SELECT,
                    name: 'ExtensionType',
                    label: 'Select type here...',
                    path: 'type',
                    options: getters.extensionOptionTypes,
                    selected: extension.type ? extension.type.charAt(0).toUpperCase() + extension.type.slice(1) : null,
                    validate: 'required',
                    disabled: operator,
                    visible: (extension: ExtensionOption) => !extension.id
                },
                {
                    type: InputType.LABEL,
                    label: 'Custom Field Options:',
                    name: 'CustomFieldOptions',
                    class: 'subtitle-1',
                    disabled: operator,
                    visible: (extension: ExtensionOption) => extension.type && extension.type.toLowerCase() === 'dropdown'
                },
                {
                    type: InputType.LABEL,
                    label: `List the dropdown options below separated by a comma. (e.g. blue team, red team, green team).`,
                    class: 'caption',
                    disabled: operator,
                    visible: (extension: ExtensionOption) => extension.type && extension.type.toLowerCase() === 'dropdown'
                },
                {
                    type: InputType.TEXTAREA,
                    name: 'ExtensionOptions',
                    path: 'data',
                    disabled: operator,
                    visible: (extension: ExtensionOption) => extension.type && extension.type.toLowerCase() === 'dropdown',
                    validate: 'required|max:32000'
                }
            ]
        },
        formDefaultInputs(state, getters): Input[] {
            //  Grab our extensions
            const extensions = getters.extensionOptions

            //  Our default fields
            const inputs = [
                {
                    type: InputType.PHONE,
                    name: "Default Phone",
                    label: "Mobile Phone",
                    path: "phone_default.free_form_number",
                    cols: 12,
                    order: 1,
                    validate: 'phone',
                    required: true,
                    disableDelete: true,
                    disableRequired: true,
                    disableInputChange: true
                },
                {
                    type: InputType.TEXT,
                    name: "Title",
                    label: "Title",
                    path: "name_title",
                    cols: 12,
                    order: 2,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "First Name",
                    label: "First Name",
                    path: "name_given",
                    cols: 12,
                    order: 3,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Middle Name",
                    label: "Middle Name",
                    path: "name_middle",
                    cols: 12,
                    order: 4,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Last Name",
                    label: "Last Name",
                    path: "name_family",
                    cols: 12,
                    order: 5,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Full Name",
                    label: "Full Name",
                    path: "name_full",
                    cols: 12,
                    order: 6,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Suffix",
                    label: "Suffix",
                    path: "name_suffix",
                    cols: 12,
                    order: 7,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Billing Address Line 1",
                    label: "Billing Line 1",
                    path: "address_billing.line1",
                    cols: 12,
                    order: 8,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Billing Address Line 2",
                    label: "Billing Line 2",
                    path: "address_billing.line2",
                    cols: 12,
                    order: 9,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Shipping Address Line 1",
                    label: "Address Line 1",
                    path: "address_shipping.line1",
                    cols: 12,
                    order: 10,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Shipping Address Line 2",
                    label: "address_shipping.line2",
                    path: "line2",
                    cols: 12,
                    order: 11,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Company",
                    label: "Company",
                    path: "company",
                    cols: 12,
                    order: 12,
                    validate: 'max:45',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Website",
                    label: "Website",
                    path: "website",
                    cols: 12,
                    order: 13,
                    validate: 'max:45|verify_url',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Email",
                    label: "Email",
                    path: "email_default.address",
                    cols: 12,
                    order: 14,
                    validate: 'email',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: "Short Answer",
                    label: "Short Answer",
                    path: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
                    cols: 12,
                    order: 15,
                    validate: 'max:300',
                    required: true
                }
            ] as Input[]

            //  If we don't have any extension options, just return our inputs
            if (!extensions) return inputs

            //  Else, iterate through the extension options and add each one
            extensions.forEach((option) => {
                inputs.push({
                    type: option.type.toUpperCase(),
                    name: option.name,
                    label: option.name,
                    path: `extensions.${option.name}`,
                    options: option.type === 'dropdown' ? option.data : null,
                    cols: 12,
                    order: 16,
                    validate: (
                        {
                            number: 'max:45|numeric',
                            phone: 'phone'
                        }[option.type] || 'max:75'
                    ),
                    required: true
                })
            })

            //  Return all our inputs now
            return inputs
        },
        personCreateInputs: (state, getters) => (optional: boolean = false): Input[] => {
            return [
                {
                    type: InputType.TEXT,
                    name: 'Mobile Phone',
                    label: 'Mobile Phone',
                    path: 'phone_default.free_form_number',
                    mask: '(###) ###-####',
                    validate: 'required|phone',
                    required: true
                },
                {
                    type: InputType.TEXT,
                    name: 'First Name',
                    label: `First Name ${optional ? '(optional)' : ''}`,
                    path: 'name_given',
                    validate: `${optional ? '' : 'required|'}max:45`,
                    required: optional ? false : true
                },
                {
                    type: InputType.TEXT,
                    name: 'Last Name',
                    label: `Last Name ${optional ? '(optional)' : ''}`,
                    path: 'name_family',
                    validate: `${optional ? '' : 'required|'}max:45`,
                    required: optional ? false : true
                }
            ]
        },
        personCreateMinInputs(): Input[] {
            return [
                {
                    type: InputType.TEXT,
                    name: 'Mobile Phone',
                    label: 'Mobile Phone',
                    path: 'phone_default.free_form_number',
                    mask: '(###) ###-####',
                    validate: 'required|phone',
                    required: true
                }
            ]
        },
        personCustomFieldsInputs: () => (person: Person, options: ExtensionOption[]): Input[] => {
            //  Fetch the person's extensions
            const extensions = person.extensions

            //  Create the inputs container
            const inputs = []

            //  For each extension, bind input properties
            options.forEach(option => {
                //  Select a validation rule
                const rule = {
                    phone: 'phone|max:20',
                    // date: 'date_format:MM/dd/yyyy',
                    text: 'max:75',
                    number: 'decimal',
                    dropdown: null
                }[option.type]

                //  Select an icon
                const icon = {
                    phone: 'mdi-phone',
                    date: 'mdi-calendar-month',
                    text: 'mdi-text-shadow',
                    number: 'mdi-pound-box',
                    dropdown: 'mdi-format-list-bulleted'
                }[option.type]

                //  Select the option type
                const type = {
                    phone: InputType.PHONE,
                    date: InputType.DATE,
                    text: InputType.TEXT,
                    number: InputType.TEXT,
                    dropdown: InputType.SELECT
                }[option.type]

                //  Push the extensions input
                inputs.push({
                    type: type,
                    name: option.name,
                    label: option.name,
                    options: option.data || null,
                    selected: extensions?.[option.name] || null,
                    path: `${option.name}`,
                    prependIcon: icon,
                    validate: rule
                })
            })

            //  Return the inputs
            return inputs
        },
        personEmailsInputs: (state, getters) => (emails: Email[]): Input[] => {
            //  Create a container for our inputs
            const inputs = []

            //  For each email, create an input
            for (let i = 0; i < emails.length; i++) {
                inputs.push({
                    type: InputType.TEXT,
                    name: 'Email',
                    label: 'Email',
                    path: `emails.${i}.address`,
                    validate: 'email'
                })
            }

            return inputs
        },
        personNamesInputs(state, getters): Input[] {
            const required = getters.dataProvider === 'XERO' || getters.dataProvider === 'QBO'

            return [
                {
                    type: InputType.TEXT,
                    name: 'Full Name',
                    label: 'Full Name',
                    path: 'name_full',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'Title',
                    label: 'Title',
                    path: 'name_title',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'First Name',
                    label: 'First Name',
                    path: 'name_given',
                    validate: `${required ? 'required|' : ''}max:45`
                },
                {
                    type: InputType.TEXT,
                    name: 'Middle Name',
                    label: 'Middle Name',
                    path: 'name_middle',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'Last Name',
                    label: 'Last Name',
                    path: 'name_family',
                    validate: `${required ? 'required|' : ''}max:45`
                },
                {
                    type: InputType.TEXT,
                    name: 'Suffix',
                    label: 'Suffix',
                    path: 'name_suffix',
                    validate: 'max:20'
                }
            ]
        },
        personMoreInfoInputs(): Input[] {
            return [
                {
                    type: InputType.TEXT,
                    name: 'Company',
                    label: 'Company',
                    path: 'company',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'Website',
                    label: 'Website',
                    path: 'website',
                    validate: 'verify_url|max:100'
                },
                {
                    type: InputType.TEXT,
                    name: 'Email',
                    label: 'Email',
                    path: 'email_default.address',
                    validate: 'email'
                },
            ]
        },
        personNotesInputs(): Input[] {
            return [
                {
                    type: InputType.TEXTAREA,
                    name: 'Notes',
                    label: 'Notes',
                    path: 'notes',
                    height: '100%',
                    class: 'mt-4',
                    validate: ''
                }
            ]
        },
        personProfileInputs: (state, getters) => (person: Person): Input[] => {
            const inputs = [
                {
                    type: InputType.TEXT,
                    name: 'Display Name',
                    label: 'Display Name',
                    path: 'name_full',
                    validate: 'required|max:90'
                },
                {
                    type: InputType.TEXT,
                    name: 'Company',
                    label: 'Company',
                    path: 'company',
                    validate: 'max:45'
                },
                {
                    type: InputType.TEXT,
                    name: 'Website',
                    label: 'Website',
                    path: 'website',
                    validate: 'verify_url'
                }
            ]

            return inputs
        },
        templateInputs: (state, getters) => (template: Template): Input[] => {
            //  Grab the tenant data provider
            const provider = getters?.tenant?.data_provider

            const items = [
                {
                    type: InputType.TEXT,
                    name: 'Template Name',
                    label: 'Template Name',
                    icon: 'mdi-format-size',
                    path: 'template_name',
                    validate: 'required|max:45',
                    required: true
                },
                {
                    type: InputType.MESSAGE_TEXT_FORM,
                    name: 'Template Body',
                    label: 'Template Body',
                    path: 'message_body',
                    validate: getters.templateBodyValidation(template),
                    required: true
                }
            ] as Input[]

            //  If we our provider is QBO or XERO, add the select
            if (provider === 'XERO' || provider === 'QBO') {
                items.unshift({
                    type: InputType.SELECT,
                    name: 'Template Type',
                    label: 'Template Type',
                    path: 'type',
                    options: [
                        {
                            text: "Message",
                            value: "message"
                        },
                        {
                            text: "Invoice",
                            value: "invoice"
                        }
                    ],
                    selected: template.type,
                    validate: 'required'
                })
            }

            //  Return our items
            return items
        }
    }
}
